import React, { useState, useRef } from 'react';
import SEO from './SEO';
import { generateOrganizationSchema, generateWebsiteSchema } from '../utils/structuredData';

// Endpoint del CMS que procesa la inscripción (ruta de plugin sin CSRF que devuelve JSON).
// Definido en plugins/remiblot/cmsadditions/routes-pf.php del CMS Winter.
const PF_ENDPOINT = 'https://discover.af-pictures.com/api/pf-submit';
// URL del PDF con las bases legales del concurso. Actualízala con la ruta real del PDF subido al CMS.
const BASES_PDF_URL = 'https://afpictures.com/storage/app/media/AF%20-%20Bases%20de%20la%20I%20EDICI%C3%93N%20DEL%20CONCURSO%20DE%20GUIONES%20DE%20CORTOMETRAJES.pdf';

const GENERO_OPTIONS = ['Femenino', 'Masculino', 'No binario/otro', 'Prefiero no decirlo'];

const GUIONISTA_FIELDS = [
  { name: 'nombre', label: 'Nombre', type: 'text' },
  { name: 'apellidos', label: 'Apellidos', type: 'text' },
  { name: 'nacionalidad', label: 'Nacionalidad', type: 'text' },
  { name: 'fecha_nacimiento', label: 'Fecha de nacimiento', type: 'date' },
  { name: 'domicilio', label: 'Domicilio', type: 'text' },
  { name: 'localidad', label: 'Localidad', type: 'text' },
  { name: 'cp', label: 'Código Postal', type: 'text' },
  { name: 'pais', label: 'País', type: 'text' },
  { name: 'genero', label: 'Género', type: 'select' },
  { name: 'centro_estudios', label: 'Centro de estudios', type: 'text' },
  { name: 'estudios', label: 'Estudios', type: 'text' },
  { name: 'dni', label: 'DNI', type: 'text' },
  { name: 'telefono', label: 'Teléfono', type: 'tel' },
  { name: 'email', label: 'Mail', type: 'email' },
];

function ConcursoGuiones(props) {

  const featuredImage = 'https://discover.af-pictures.com/storage/app/media/upload-img/Poster%20Horizontal%20Summer%20Screams.png';

  const organizationSchema = generateOrganizationSchema();
  const websiteSchema = generateWebsiteSchema();
  const combinedSchema = [organizationSchema, websiteSchema];

  const formRef = useRef(null);
  const mountedAt = useRef(Date.now());
  const [dosGuionistas, setDosGuionistas] = useState(false);
  const [basesOpened, setBasesOpened] = useState(false);
  const [basesAccepted, setBasesAccepted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: '', text: '' });
  const [missingFields, setMissingFields] = useState([]);

  const inputClass = 'w-full px-4 py-2 text-white bg-zinc-800 border border-gray-800 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors';
  const fileClass = 'block w-full text-sm text-gray-300 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-zinc-700 file:text-white hover:file:bg-zinc-600';

  const isFieldEmpty = (el) => {
    if (el.type === 'file') return el.files.length === 0;
    if (el.tagName === 'SELECT') return el.value.trim() === '';
    if (el.type === 'checkbox') return !el.checked;
    return el.value.trim() === '';
  };

  const renderGuionistaFields = (suffix, required) =>
    GUIONISTA_FIELDS.map((f) => (
      <div key={`${f.name}_${suffix}`}>
        <label className="block text-sm font-medium text-gray-100 mb-2">
          {f.label} {required && <span className="text-red-500">*</span>}
        </label>
        {f.type === 'select' ? (
          <select name={`${f.name}_${suffix}`} required={required} data-label={f.label} className={inputClass}>
            <option value="">Seleccionar...</option>
            {GENERO_OPTIONS.map((o) => (
              <option key={o}>{o}</option>
            ))}
          </select>
        ) : (
          <input type={f.type} name={`${f.name}_${suffix}`} required={required} data-label={f.label} className={inputClass} />
        )}
      </div>
    ));

  const renderFile = (name, label, required, accept, hint) => (
    <div>
      <label className="block text-sm font-medium text-gray-100 mb-2">
        {label}{' '}
        <span className="text-gray-500 text-xs">({accept.includes('jpg') ? 'PDF o JPG' : 'PDF'})</span>{' '}
        {required && <span className="text-red-500">*</span>}
      </label>
      {hint && <p className="text-xs text-gray-500 mb-2">{hint}</p>}
      <input type="file" name={name} required={required} accept={accept} data-label={label} className={fileClass} />
    </div>
  );

  const handleFieldFix = (e) => {
    const el = e.target;
    if (el.hasAttribute && el.hasAttribute('required') && !isFieldEmpty(el)) {
      el.classList.remove('ring-2', 'ring-red-500');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = formRef.current;
    setSubmitStatus({ type: '', text: '' });
    setMissingFields([]);

    if (!basesOpened) {
      setSubmitStatus({ type: 'error', text: 'Debes abrir y leer las bases del concurso antes de enviar la inscripción.' });
      return;
    }
    if (!basesAccepted) {
      setSubmitStatus({ type: 'error', text: 'Debes aceptar las bases del concurso para poder enviar la inscripción.' });
      return;
    }

    const requiredFields = Array.from(form.querySelectorAll('[required]')).filter((el) => el.offsetParent !== null);
    requiredFields.forEach((el) => el.classList.remove('ring-2', 'ring-red-500'));
    const missing = requiredFields.filter(isFieldEmpty);
    if (missing.length > 0) {
      missing.forEach((el) => el.classList.add('ring-2', 'ring-red-500'));
      setMissingFields(missing.map((el) => el.getAttribute('data-label') || el.name));
      missing[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await fetch(PF_ENDPOINT, {
        method: 'POST',
        body: new FormData(form),
      });
      let data = {};
      try {
        data = await res.json();
      } catch (_) {
        data = {};
      }
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'No se pudo enviar la inscripción.');
      }
      setSubmitStatus({
        type: 'success',
        text: data.message || 'Hemos recibido tu inscripción. Revisa tu correo por si necesitamos contactarte.',
      });
      form.reset();
      setDosGuionistas(false);
      setBasesAccepted(false);
      setBasesOpened(false);
    } catch (err) {
      setSubmitStatus({
        type: 'error',
        text:
          err && err.message
            ? err.message
            : 'No se pudo enviar la inscripción. Inténtalo de nuevo en unos minutos o escríbenos a afclub@afpictures.com.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEO
        title='Concurso de Guiones - Summer Screams'
        description='En AF creemos en el talento emergente. Lanzamos un concurso dirigido a estudiantes de cine y audiovisuales para presentar sus mejores guiones de cortometraje de terror. Premio de 3.000€.'
        keywords='concurso de guiones, cortometraje de terror, guionistas, AF Pictures, concurso cine, talento emergente'
        url='https://afpictures.com/concurso-guiones'
        image='https://afpictures.com/img/af-pictures-og.jpg'
        structuredData={combinedSchema}
      />

      <div style={{ backgroundImage: 'url(' + featuredImage + ')',backgroundPosition:'center', backgroundSize:'cover'}} className="relative background-cover items-center h-[40vh]">
                <h1 style={{fontSize:'200%'}} className="text-center w-full pt-[20vh]  font-semibold text-white">CONCURSO DE GUIONES</h1>
    </div>
      <div className={"w-full bg-zinc-900 text-white " + props.transition}>
        <div className="max-w-[820px] mx-auto px-4 md:px-8 py-12 lg:py-20">

          {/* Header */}
          <div className="text-center mb-10">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Summer Screams</h1>
            <h2 className="text-2xl md:text-3xl font-semibold">Concurso de Guiones</h2>
          </div>

          {/* Intro */}
          <div className="space-y-6 text-lg md:text-xl text-gray-200 leading-relaxed text-center mb-12">
            <p>
              En AF creemos en el talento emergente y queremos ayudar a descubrir a la próxima
              generación de guionistas. Por eso lanzamos un concurso dirigido a estudiantes de cine
              y audiovisuales, invitándoles a presentar sus mejores guiones de cortometraje de terror.
            </p>
            <p>
              El terror se ha consolidado como uno de los géneros favoritos del público joven y una
              de las grandes puertas de entrada para nuevos creadores. Buscamos historias originales,
              capaces de sorprender, inquietar y dejar huella, con el objetivo de encontrar nuevos
              talentos y dar visibilidad a las voces que marcarán el futuro del género.
            </p>
            <p>
              Con esta iniciativa queremos impulsar el inicio de la carrera de nuevos guionistas,
              ofreciéndoles la oportunidad de desarrollar su talento con el respaldo y la experiencia
              de AF, una de las productoras españolas con mayor proyección y crecimiento en el mercado
              internacional. El proyecto seleccionado podrá convertirse en el punto de partida de una
              futura producción audiovisual.
            </p>
          </div>

          {/* Prize highlight */}
          <div className="border-t border-b border-white/40 py-8 text-center mb-4">
            <h3 className="text-2xl md:text-3xl font-bold">
              El ganador del concurso será premiado con
              <br />
              un premio de 3.000€ en metálico
            </h3>
          </div>
          <p className="text-center italic text-gray-400 mb-16">
            Más abajo encontrarás toda la información sobre el concurso
          </p>

          {/* En qué consiste */}
          <section className="mb-14">
            <h3 className="text-2xl font-bold text-center mb-6">En qué consiste</h3>
            <ul className="list-disc pl-6 space-y-4 text-gray-200 leading-relaxed">
              <li>
                Buscamos guiones de cortometrajes de terror de entre 20-30 minutos. Toda la historia
                deberá ocurrir en una de estas tres posibles localizaciones: un HOSPITAL, una CASA o
                un EDIFICIO ABANDONADO.
              </li>
              <li>Deben ser exclusivamente de ficción y no se considerarán cortos de animación o documental.</li>
              <li>Los guiones deben ser en castellano o en inglés.</li>
              <li>
                Los participantes deben cursar actualmente estudios de cine o de audiovisual (de
                cualquier disciplina) en alguna escuela de cine/universidad/centro de formación
                profesional del país o bien haber terminado sus estudios en el curso 2025-2026.
              </li>
              <li>
                El guion puede ser presentado por un máximo de dos autores, en este caso el premio
                será repartido entre ambos a partes iguales.
              </li>
              <li>Los autores deben residir en España y ser personas físicas mayores de edad.</li>
              <li>
                Las obras deben ser originales e inéditas, esto es, obras no producidas ni explotadas
                de cualquier otra forma. Asimismo, tampoco podrán presentarse guiones que en el momento
                de su envío a este concurso estén pendientes de la resolución de cualquier otro concurso
                similar o en un laboratorio de desarrollo. Tampoco guiones que hayan sido premiados en
                concursos previos.
              </li>
              <li>
                El/La participante deberá ser titular único de todos los derechos de propiedad
                intelectual sobre el guion que se presente al concurso. En caso de ser coautores,
                deberán ser titulares de todos los derechos conjuntamente.
              </li>
              <li>
                Cada participante podrá enviar un <strong>máximo de 3 guiones a la convocatoria</strong>.
                Únicamente se premiará 1 de los guiones presentados.
              </li>
            </ul>
          </section>

          {/* Documentación requerida */}
          <section className="mb-14 border-t border-white/40 pt-14">
            <h3 className="text-2xl font-bold text-center mb-6">Documentación requerida</h3>
            <ol className="list-decimal pl-6 space-y-4 text-gray-200 leading-relaxed">
              <li>
                Guion entre 20 y 30 páginas, sin incluir portada.
                <ul className="list-disc pl-6 mt-3 space-y-2">
                  <li>Se estima una página de guion en formato estándar por minuto de obra audiovisual.</li>
                  <li>
                    Se debe presentar en formato profesional, siguiendo las convenciones de la
                    industria; <strong>Tipografía: Courier, Tamaño: 12 o similar, Interlineado doble.</strong>
                  </li>
                </ul>
              </li>
              <li>Memoria de guion donde se incluya las intenciones y bases creativas del proyecto.</li>
              <li>CV del guionista/s.</li>
              <li>Comprobante de estudios.</li>
              <li>
                Comprobante o resguardo de la inscripción del guion en el Registro de la Propiedad
                intelectual o en plataformas de registro de derechos de autor.
              </li>
              <li>Copia del DNI, NIE o pasaporte.</li>
              <li>
                En el caso de co-autoría: <strong>DECLARACIÓN DE CONFORMIDAD</strong> firmado por todas
                las partes para garantizar el acuerdo de la inscripción en la presente convocatoria.
              </li>
            </ol>
            <p className="text-center text-gray-300 mt-6">
              En caso de no presentar alguno de los documentos referidos, o que los mismos no se ajusten
              a lo estipulado, el proyecto será automáticamente descalificado.
            </p>
          </section>

          {/* Premio */}
          <section className="mb-14 border-t border-white/40 pt-14 text-center">
            <h3 className="text-2xl font-bold mb-4">Premio</h3>
            <p className="text-gray-200 leading-relaxed">
              Premio en metálico: <strong>premio de 3.000 euros</strong>. En caso de co-autoría será
              dividido a partes iguales, cuyas cuantías serán sometidas a la tributación que marque la
              legislación vigente.
            </p>
          </section>

          {/* Jurado */}
          <section className="mb-14 border-t border-white/40 pt-14 text-center">
            <h3 className="text-2xl font-bold mb-4">El jurado del concurso</h3>
            <div className="space-y-3 text-gray-200 leading-relaxed">
              <p>
                Un jurado formado íntegramente por el equipo de contenidos de la productora será
                encargado de elegir el proyecto ganador.
              </p>
              <p>Las deliberaciones y votaciones del jurado serán confidenciales.</p>
              <p>La elección del guion seleccionado, así como el ganador, tendrá carácter inapelable.</p>
            </div>
          </section>

          {/* Plazo y resolución */}
          <section className="mb-16 border-t border-white/40 pt-14 text-center">
            <h3 className="text-2xl font-bold mb-4">Plazo y resolución</h3>
            <div className="space-y-3 text-gray-200 leading-relaxed">
              <p>
                El plazo para el envío de guiones será desde el{' '}
                <strong>22 de julio hasta el 9 de septiembre</strong>. Ambos días hábiles.
              </p>
              <p>
                El/La ganador del concurso será contactado vía mail el <strong>16 de octubre</strong>.
              </p>
              <p className="font-bold underline">No se aceptarán guiones fuera de plazo.</p>
            </div>
          </section>

          {/* Formulario */}
          <section id="formulario" className="border-t border-white/40 pt-14">
            <div className="text-center mb-8">
              <h3 className="text-3xl font-semibold mb-2">Inscripción</h3>
              <p className="italic text-gray-400">Imprescindible leer y aceptar las bases completas</p>
            </div>

            <div className="w-full max-w-2xl mx-auto bg-zinc-900 rounded-lg shadow-lg p-4 md:p-8 border border-white/10">
              {submitStatus.type === 'success' ? (
                <div className="py-12 text-center">
                  <div className="mx-auto mb-4 flex items-center justify-center w-16 h-16 rounded-full bg-green-900 border border-green-700">
                    <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="text-2xl font-semibold text-white mb-2">Inscripción enviada</h3>
                  <p className="text-green-200 max-w-md mx-auto">{submitStatus.text}</p>
                </div>
              ) : (
                <>
                  <p className="text-sm text-white mb-6 text-center">
                    <span className="text-red-500">*</span> indica campo obligatorio
                  </p>

                  {submitStatus.type === 'error' && (
                    <div className="mb-6 rounded-md border border-red-800 bg-red-950 text-red-200 text-sm p-4">
                      {submitStatus.text}
                    </div>
                  )}
                  {missingFields.length > 0 && (
                    <div className="mb-6 rounded-md border border-red-800 bg-red-950 text-red-200 text-sm p-4">
                      Faltan campos obligatorios: {missingFields.join(', ')}
                    </div>
                  )}

                  <form
                    ref={formRef}
                    onSubmit={handleSubmit}
                    onInput={handleFieldFix}
                    onChange={handleFieldFix}
                    className="space-y-6 text-white"
                    noValidate
                  >
                    <input type="text" name="web" tabIndex="-1" autoComplete="off" className="hidden" aria-hidden="true" />
                    <input type="hidden" name="form_ts" defaultValue={mountedAt.current} />

                    <div className="pt-2">
                      <h3 className="text-lg font-semibold text-white mb-4 border-b border-gray-800 pb-2">Datos del guionista 1</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {renderGuionistaFields('1', true)}
                      </div>
                    </div>

                    <label className="flex items-center space-x-3 cursor-pointer pt-2">
                      <input
                        type="checkbox"
                        name="dos_guionistas"
                        value="1"
                        checked={dosGuionistas}
                        onChange={(e) => setDosGuionistas(e.target.checked)}
                        className="w-4 h-4 text-blue-600 bg-zinc-800 border-gray-600 rounded focus:ring-blue-500 focus:ring-2"
                      />
                      <span className="text-sm text-gray-100">Hay un segundo guionista</span>
                    </label>

                    {dosGuionistas && (
                      <div className="pt-2">
                        <h3 className="text-lg font-semibold text-white mb-4 border-b border-gray-800 pb-2">Datos del guionista 2</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {renderGuionistaFields('2', true)}
                        </div>
                      </div>
                    )}

                    <div className="pt-2">
                      <h3 className="text-lg font-semibold text-white mb-4 border-b border-gray-800 pb-2">Datos del proyecto</h3>
                      <label className="block text-sm font-medium text-gray-100 mb-2">
                        Título del proyecto <span className="text-red-500">*</span>
                      </label>
                      <input type="text" name="titulo_proyecto" required data-label="Título del proyecto" className={inputClass} />
                      <p className="mt-1 text-xs text-gray-500">Si son varios, separar los títulos con un punto y coma (;)</p>
                    </div>

                    <div className="pt-2">
                      <h3 className="text-lg font-semibold text-white mb-4 border-b border-gray-800 pb-2">Documentación a adjuntar</h3>
                      <div className="space-y-4">
                        {renderFile('file_dni_1', 'Copia DNI o pasaporte — guionista 1', true, '.pdf,.jpg,.jpeg')}
                        {dosGuionistas && renderFile('file_dni_2', 'Copia DNI o pasaporte — guionista 2', true, '.pdf,.jpg,.jpeg')}
                        {renderFile('file_estudios_1', 'Comprobante de estudios — guionista 1', true, '.pdf,.jpg,.jpeg')}
                        {dosGuionistas && renderFile('file_estudios_2', 'Comprobante de estudios — guionista 2', true, '.pdf,.jpg,.jpeg')}
                        {renderFile('file_guion_1', 'Guion 1', true, '.pdf')}
                        {renderFile('file_guion_2', 'Guion 2 (opcional, si el proyecto tiene varios títulos)', false, '.pdf')}
                        {renderFile('file_guion_3', 'Guion 3 (opcional)', false, '.pdf')}
                        {renderFile('file_memoria', 'Memoria (intenciones y bases creativas del proyecto)', true, '.pdf')}
                        {renderFile('file_cv_1', 'CV del guionista 1', true, '.pdf')}
                        {dosGuionistas && renderFile('file_cv_2', 'CV del guionista 2', true, '.pdf')}
                        {renderFile('file_registro', 'Comprobante de inscripción en el Registro de la Propiedad Intelectual', true, '.pdf')}
                        {dosGuionistas &&
                          renderFile(
                            'file_declaracion',
                            'Declaración de conformidad (firmada por ambos guionistas)',
                            true,
                            '.pdf',
                            'Obligatorio en caso de participación colectiva, indicando el representante designado.'
                          )}
                      </div>
                    </div>

                    {/* Bases del concurso: aceptar y enviar solo aparecen tras abrir el PDF */}
                    <div className="pt-4 border-t border-gray-800">
                      {basesOpened ? (
                        <h3 className="text-lg font-semibold text-white mb-2">Bases del concurso</h3>
                      ) : (
                        <h3 className="text-2xl font-bold text-amber-400 text-center mb-3">
                          Último paso: lee las bases legales del concurso
                        </h3>
                      )}
                      {!basesOpened && (
                        <p className="text-sm text-gray-300 mb-4">
                          Para poder enviar tu inscripción es imprescindible leer las bases legales.
                          Ábrelas con el botón de abajo y, al volver, podrás aceptarlas y enviar el formulario.
                        </p>
                      )}
                      <a
                        href={BASES_PDF_URL}
                        target="_blank"
                        rel="noreferrer"
                        onClick={() => setBasesOpened(true)}
                        className={
                          basesOpened
                            ? 'inline-flex items-center gap-2 mb-4 px-4 py-2 rounded-md border border-white/30 text-white hover:bg-white/10 transition-colors'
                            : 'flex w-full items-center justify-center gap-2 mb-2 px-6 py-3 rounded-md bg-[#735707] uppercase text-white font-medium hover:opacity-90 transition-colors'
                        }
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                        </svg>
                        Leer las bases legales del concurso (PDF)
                      </a>
                      {!basesOpened && (
                        <p className="text-xs text-gray-500 text-center">
                          Se abrirán en una pestaña nueva; esta página no se pierde.
                        </p>
                      )}

                      {basesOpened && (
                        <label className="fadeIn flex items-start space-x-3 cursor-pointer">
                          <input
                            type="checkbox"
                            name="bases_aceptadas"
                            value="1"
                            checked={basesAccepted}
                            onChange={(e) => setBasesAccepted(e.target.checked)}
                            className="mt-1 w-4 h-4 text-blue-600 bg-zinc-800 border-gray-600 rounded focus:ring-blue-500 focus:ring-2"
                          />
                          <span className="text-sm text-gray-100">
                            He leído y acepto las bases legales del concurso <span className="text-red-500">*</span>
                          </span>
                        </label>
                      )}
                    </div>

                    {basesOpened && (
                      <button
                        type="submit"
                        disabled={isSubmitting || !basesAccepted}
                        className="fadeIn w-full bg-[#735707] uppercase text-white py-3 px-6 rounded-md font-medium hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-zinc-900 focus:ring-blue-500 transition-colors"
                      >
                        {isSubmitting ? 'Enviando...' : 'Enviar inscripción'}
                      </button>
                    )}
                  </form>
                </>
              )}
            </div>
          </section>

        </div>
      </div>
    </>
  );
}

export default ConcursoGuiones;
